/**
 * Servicio de evaluación académica
 * Evalúa conversaciones usando rúbricas cuando se completa 100% de objetivos medibles
 * 
 * IMPORTANTE - Cálculo de porcentajes:
 * - maxScore de cada elemento = suma de los puntos de todos sus criterios
 * - NUNCA usar element.weight o rubricWeights para calcular maxScore
 * - El weight solo se usa para ponderar el porcentaje final en calculateOverallScore
 * - Ejemplo: Pronunciación tiene 1 criterio con 15 puntos → maxScore = 15 (NO usar weight = 10)
 */

import { supabaseService } from './supabaseService';
import curriculumData from '../../curriculum_data.json';
import llmCredentialsService from './llmCredentialsService';
import { RUBRIC_WEIGHTS } from '../config/rubricConfig';

export interface RubricElement {
  name: string;
  weight: number;
  criteria: Array<{
    description: string;
    points: number;
    evaluation_guide: string;
  }>;
}

export interface RubricEvaluation {
  overall_score: number;
  timestamp: string;
  elements: Array<{
    name: string;
    score: number;
    max_score: number;
    percentage: number;
    feedback: string;
    criteria_scores: Array<{
      description: string;
      score: number;
      max_score: number;
      feedback?: string;
    }>;
  }>;
  conversation_id?: string;
  evaluated_by: string;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Rúbrica por defecto cuando curriculum_data.json no tiene rubric_template (p. ej. actividades ACTIVIDAD_*) */
function getDefaultRubricTemplate(): { elements: RubricElement[] } {
  const elements: RubricElement[] = [
    {
      name: 'Fluidez comunicativa',
      weight: 25,
      criteria: [{
        description: 'Ritmo natural, respuestas sin bloqueos largos y contribución activa al diálogo',
        points: 25,
        evaluation_guide: 'Evalúa si el estudiante responde con naturalidad, sin pausas excesivas, y contribuye a mantener el diálogo.',
      }],
    },
    {
      name: 'Vocabulario',
      weight: 25,
      criteria: [{
        description: 'Riqueza y pertinencia de las palabras en el contexto de la tarea',
        points: 25,
        evaluation_guide: 'Evalúa la riqueza y pertinencia del vocabulario usado por el estudiante.',
      }],
    },
    {
      name: 'Gramática',
      weight: 25,
      criteria: [{
        description: 'Estructuras, tiempos verbales y concordancia correctas',
        points: 25,
        evaluation_guide: 'Evalúa la corrección gramatical de las producciones del estudiante.',
      }],
    },
    {
      name: 'Pronunciación',
      weight: 25,
      criteria: [{
        description: 'Claridad y corrección fonética',
        points: 25,
        evaluation_guide: 'Evalúa la claridad y la corrección fonética en las respuestas habladas del estudiante (solo mensajes de audio).',
      }],
    },
  ];
  return { elements };
}

class AcademicEvaluationService {
  /**
   * Evalúa una conversación completa usando la rúbrica
   * Solo se ejecuta cuando se completa el 100% de los objetivos medibles
   */
  async evaluateWithRubric(
    conversation: ConversationMessage[],
    level: string,
    unit: number,
    lesson: number,
    _objectivesStatus: Record<string, any>,
    taskDescription?: string,
    objectives?: Array<{ description: string }>
  ): Promise<RubricEvaluation | null> {
    try {
      // Usar pesos fijos de la configuración
      const rubricWeights = RUBRIC_WEIGHTS;
      let rubricTemplate = (curriculumData as any).rubric_template;
      // Fallback: curriculum_data.json puede no tener rubric_template (p. ej. actividades ACTIVIDAD_*)
      if (!rubricTemplate?.elements?.length) {
        rubricTemplate = getDefaultRubricTemplate();
      }

      // Evaluar cada elemento de la rúbrica
      const elementEvaluations = await Promise.all(
        rubricTemplate.elements.map(async (element: RubricElement) => {
          // Usar peso de la configuración fija, con fallback al default_weight o weight del template
          const weight = rubricWeights[element.name] !== undefined && rubricWeights[element.name] !== null
            ? rubricWeights[element.name]
            : ((element as any).default_weight !== undefined ? (element as any).default_weight : element.weight);
          
          // console.log(
          //   `[AcademicEvaluationService] Elemento "${element.name}": ` +
          //   `peso usado = ${weight} (desde RUBRIC_WEIGHTS)`
          // );

          const evaluation = await this.evaluateRubricElement(
            element,
            conversation,
            level,
            unit,
            lesson,
            weight,
            taskDescription,
            objectives
          );
          // Incluir el weight en la evaluación para usarlo en el cálculo ponderado
          return { ...evaluation, weight };
        })
      );

      // Calcular puntuación total ponderada
      const overallScore = this.calculateOverallScore(elementEvaluations, rubricWeights);

      const evaluation: RubricEvaluation = {
        overall_score: overallScore,
        timestamp: new Date().toISOString(),
        elements: elementEvaluations,
        evaluated_by: 'llm+criteria',
      };

      return evaluation;
    } catch (error) {
      console.error('[AcademicEvaluationService] Error al evaluar con rúbrica:', error);
      return null;
    }
  }

  /**
   * Evalúa un elemento específico de la rúbrica
   * 
   * IMPORTANTE: maxScore SIEMPRE debe ser la suma de los puntos de los criterios,
   * NUNCA usar el weight. El weight solo se usa para ponderar el porcentaje final.
   */
  private async evaluateRubricElement(
    element: RubricElement,
    conversation: ConversationMessage[],
    level: string,
    unit: number,
    lesson: number,
    _weight: number, // No se usa en este método, solo se pasa para compatibilidad. El weight se usa en calculateOverallScore
    taskDescription?: string,
    objectives?: Array<{ description: string }>
  ): Promise<{ name: string; score: number; max_score: number; percentage: number; feedback: string; criteria_scores: any[] }> {
    // Por ahora, evaluación básica
    // TODO: Integrar con LLM para evaluación más sofisticada

    // CRÍTICO: maxScore debe ser la suma de los puntos máximos de todos los criterios
    // NO usar element.weight ni _weight aquí - eso causaría porcentajes > 100%
    // Ejemplo: Pronunciación tiene 1 criterio con 15 puntos, entonces maxScore = 15
    // Si usáramos weight (10), tendríamos: percentage = (15/10)*100 = 150% ❌
    const maxScore = element.criteria.reduce((sum, criterion) => sum + criterion.points, 0);
    
    // Validar que maxScore sea válido
    if (maxScore <= 0) {
      // console.error(`[AcademicEvaluationService] maxScore inválido para ${element.name}: ${maxScore}`);
      return {
        name: element.name,
        score: 0,
        max_score: 0,
        percentage: 0,
        feedback: 'Error en cálculo de puntuación',
        criteria_scores: [],
      };
    }

    // Pronunciación: requiere mensajes de audio para poder evaluar; sin audio = 0%
    const userContents = conversation.filter((m) => m.role === 'user').map((m) => m.content || '');
    const hasAnyAudio = userContents.some((c) => /^\[audio\]\s*/i.test(c.trim()));
    if (element.name === 'Pronunciación' && !hasAnyAudio) {
      const noAudioFeedback = 'No hay respuestas de audio en esta conversación. La pronunciación requiere mensajes de voz para poder evaluarse.';
      return {
        name: element.name,
        score: 0,
        max_score: maxScore,
        percentage: 0,
        feedback: noAudioFeedback,
        criteria_scores: element.criteria.map((c) => ({
          description: c.description,
          score: 0,
          max_score: c.points,
          feedback: noAudioFeedback,
        })),
      };
    }

    let totalScore = 0;
    const criteriaScores: any[] = [];
    const feedbacks: string[] = [];

    // Evaluar cada criterio
    for (const criterion of element.criteria) {
      const criterionScore = await this.evaluateCriterion(
        criterion,
        conversation,
        level,
        unit,
        lesson,
        taskDescription,
        objectives
      );
      
      // Asegurar que el score del criterio no exceda sus puntos máximos
      const clampedCriterionScore = Math.min(criterionScore, criterion.points);
      
      criteriaScores.push({
        description: criterion.description,
        score: clampedCriterionScore,
        max_score: criterion.points,
        feedback: clampedCriterionScore >= criterion.points * 0.8 ? 'Bien' : 'Necesita mejorar',
      });

      totalScore += clampedCriterionScore;
      feedbacks.push(`${criterion.description}: ${clampedCriterionScore}/${criterion.points}`);
    }

    // Asegurar que totalScore no exceda maxScore (doble verificación)
    const clampedTotalScore = Math.min(totalScore, maxScore);


    // Calcular porcentaje con triple verificación para asegurar que nunca exceda 100%
    let percentage = 0;
    if (maxScore > 0) {
      const rawPercentage = (clampedTotalScore / maxScore) * 100;
      // Primera limitación
      percentage = Math.min(rawPercentage, 100);
      // Segunda limitación (por si hay errores de precisión)
      percentage = Math.min(percentage, 100);
    }

    // Redondear y asegurar que no exceda 100% después del redondeo
    const finalPercentage = Math.min(Math.round(percentage * 100) / 100, 100);

    return {
      name: element.name,
      score: clampedTotalScore,
      max_score: maxScore,
      percentage: finalPercentage,
      feedback: feedbacks.join('. '),
      criteria_scores: criteriaScores,
    };
  }

  /**
   * Evalúa un criterio específico usando LLM
   * IMPORTANTE: Solo evalúa las respuestas del USUARIO, no las del asistente
   */
  private async evaluateCriterion(
    criterion: { description: string; points: number; evaluation_guide: string },
    conversation: ConversationMessage[],
    _level: string,
    _unit: number,
    _lesson: number,
    taskDescription?: string,
    objectives?: Array<{ description: string }>
  ): Promise<number> {
    try {
      // FILTRAR SOLO MENSAJES DEL USUARIO - NO evaluar al asistente
      const userMessages = conversation
        .filter(msg => msg.role === 'user')
        .map(msg => msg.content);

      if (userMessages.length === 0) {
        return 0; // Sin mensajes del usuario, puntuación 0
      }

      // Criterio de pronunciación/fonética: requiere audio; sin audio no se puede evaluar → 0
      const isPronunciationCriterion = /pronunciación|fonética|claridad fonética/i.test(criterion.description);
      const hasAnyAudio = userMessages.some((c) => /^\[audio\]\s*/i.test((c || '').trim()));
      if (isPronunciationCriterion && !hasAnyAudio) {
        return 0;
      }

      // Obtener credenciales de OpenAI
      let credentials = llmCredentialsService.getCredentials();
      if (!credentials || !credentials.openaiKey) {
        credentials = await llmCredentialsService.loginAndGetCredentials();
      }

      if (!credentials.openaiKey) {
        // console.error('[AcademicEvaluationService] No se pudo obtener la API key de OpenAI');
        return 0;
      }

      // Construir el contexto: solo mensajes del usuario concatenados
      // El historial completo se incluye para contexto, pero se enfatiza que solo se evalúe al usuario
      const userConversationText = userMessages.join('\n\n');
      const fullConversationContext = conversation
        .map(msg => `${msg.role === 'user' ? 'ESTUDIANTE' : 'ASISTENTE'}: ${msg.content}`)
        .join('\n\n');

      // Construir contexto de tarea y objetivos si están disponibles
      let taskContext = '';
      if (taskDescription || objectives) {
        taskContext = '\n\n---\n\nCONTEXTO DE LA ACTIVIDAD:\n';
        if (taskDescription) {
          taskContext += `TAREA GENERAL DEL ESTUDIANTE:\n${taskDescription}\n\n`;
        }
        if (objectives && objectives.length > 0) {
          taskContext += `OBJETIVOS ESPECÍFICOS DE LA ACTIVIDAD:\n${objectives.map((obj, idx) => `${idx + 1}. ${obj.description}`).join('\n')}\n`;
        }
        taskContext += '\nIMPORTANTE: Considera este contexto al evaluar. El estudiante estaba trabajando en esta tarea específica y debía cumplir estos objetivos.';
      }

      // Solo gramática y pronunciación: no penalizar brevedad. Vocabulario y fluidez SÍ pueden penalizar respuestas muy cortas.
      const isCorrectnessOnlyCriterion = /gramática|gramatical|estructuras|tiempos verbales|concordancia|pronunciación|fonética|claridad fonética/i.test(criterion.description);
      const correctnessNote = isCorrectnessOnlyCriterion
        ? `
CRITERIO DE CORRECCIÓN (este criterio): Evalúa SOLO si lo que dijo el estudiante es correcto. No penalices la brevedad. Una respuesta corta pero correcta (ej. "Bonjour", "Oui") debe recibir puntuación ALTA o máxima si no hay errores.`
        : '';

      // Para pronunciación/fonética: el estudiante puede tener [audio] (hablado) y [text] (escrito). Solo evaluar [audio].
      const pronunciationNote = isPronunciationCriterion && hasAnyAudio
        ? `
PREFIJOS EN LOS MENSAJES DEL ESTUDIANTE: [audio] = respuesta hablada (transcripción de voz); [text] = respuesta escrita. Para este criterio de PRONUNCIACIÓN/FONÉTICA evalúa SOLO los mensajes marcados con [audio]; ignora los [text] (en texto escrito no hay pronunciación que evaluar).`
        : '';

      // Prompt estricto que enfatiza evaluar SOLO al usuario
      const systemPrompt = `Eres un evaluador de competencias comunicativas en francés. Evalúa ÚNICAMENTE las respuestas del ESTUDIANTE, ignorando las del ASISTENTE.${taskContext}

CRITERIO A EVALUAR:
- Descripción: ${criterion.description}
- Puntos máximos: ${criterion.points}
- Guía de evaluación: ${criterion.evaluation_guide}
${correctnessNote}
${pronunciationNote}

INSTRUCCIONES:
1. EVALÚA SOLO LAS RESPUESTAS DEL ESTUDIANTE (marcadas como "ESTUDIANTE" en el historial).
2. EVALÚA SOLO LO QUE EL ESTUDIANTE DIJO EN FRANCÉS. Si habló en español u otro idioma, esa parte NO cuenta. Si la mayor parte o toda la producción fue en español/otro idioma, la puntuación debe ser 0 (no hay francés que evaluar). Solo puntúa la producción efectiva en francés.
3. IGNORA las respuestas del ASISTENTE.
4. Para FLUIDEZ y VOCABULARIO: sí penaliza respuestas muy cortas o con poco vocabulario (ej. solo "Bonjour") — la puntuación debe reflejar riqueza/pertinencia e iniciativa comunicativa.
5. Solo para GRAMÁTICA y PRONUNCIACIÓN: no penalices la brevedad; puntúa según corrección. Respuesta corta pero correcta = puntuación alta.
6. La puntuación debe reflejar qué tan bien el ESTUDIANTE cumple con ESTE criterio específico.
7. ${taskDescription ? 'Considera el contexto de la tarea al evaluar.' : ''}

RESPONDE ÚNICAMENTE con un JSON válido:
{
  "score": <número entre 0 y ${criterion.points}>,
  "reasoning": "<explicación breve, solo sobre las respuestas del estudiante>"
}

- score: entre 0 y ${criterion.points}.
- No uses las respuestas del asistente para justificar la puntuación.`;

      const userPrompt = `Historial completo de la conversación (para contexto, pero evalúa SOLO al ESTUDIANTE):

${fullConversationContext}

---
RESPUESTAS DEL ESTUDIANTE A EVALUAR (estas son las únicas que debes evaluar):
${userConversationText}

Evalúa el criterio "${criterion.description}" considerando SOLO las respuestas del ESTUDIANTE mostradas arriba.`;

      // Llamar a OpenAI API
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.openaiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: userPrompt
            }
          ],
          temperature: 0.2, // Temperatura muy baja para evaluaciones consistentes y estrictas
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AcademicEvaluationService] OpenAI API error: ${response.status} - ${errorText}`);
        return 0;
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        // console.error('[AcademicEvaluationService] No se recibió contenido de OpenAI');
        return 0;
      }

      // Parsear JSON de respuesta
      let evaluationResult: { score: number; reasoning?: string };
      try {
        evaluationResult = JSON.parse(content);
      } catch (parseError) {
        // Intentar extraer JSON si hay texto adicional
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          evaluationResult = JSON.parse(jsonMatch[0]);
        } else {
          console.error(`[AcademicEvaluationService] No se pudo parsear JSON: ${content}`);
          return 0;
        }
      }

      // Validar y ajustar el score
      let score = evaluationResult.score || 0;
      
      // Asegurar que el score esté en el rango válido
      score = Math.max(0, Math.min(score, criterion.points));
      
      // Log para debugging
      // console.log(
      //   `[AcademicEvaluationService] Criterio "${criterion.description}": ` +
      //   `score=${score}/${criterion.points}, reasoning="${evaluationResult.reasoning || 'N/A'}"`
      // );

      return Math.round(score * 100) / 100;
    } catch (error) {
      // console.error('[AcademicEvaluationService] Error al evaluar criterio con LLM:', error);
      // En caso de error, retornar 0 en lugar de una puntuación inflada
      return 0;
    }
  }

  /**
   * Calcula la puntuación total porcentual ponderada
   * Cada elemento se pondera por su weight, y el porcentaje final es la suma ponderada de los porcentajes individuales
   * 
   * IMPORTANTE: Siempre priorizar rubricWeights[el.name] sobre el.weight guardado,
   * ya que los pesos pueden variar entre lecciones y queremos usar los pesos correctos de la lección actual.
   */
  private calculateOverallScore(
    elementEvaluations: Array<{
      score: number;
      max_score: number;
      percentage: number;
      weight?: number;
      name?: string;
    }>,
    rubricWeights?: Record<string, number>
  ): number {
    // Si no hay evaluaciones, retornar 0
    if (elementEvaluations.length === 0) {
      return 0;
    }

    // Función helper para obtener el peso correcto, priorizando rubricWeights sobre el.weight guardado
    const getElementWeight = (el: { weight?: number; name?: string }): number => {
      // CRÍTICO: Priorizar siempre rubricWeights[el.name] sobre el.weight guardado
      // Esto asegura que se usen los pesos específicos de la lección actual
      if (rubricWeights && el.name && rubricWeights[el.name] !== undefined && rubricWeights[el.name] !== null) {
        return rubricWeights[el.name];
      }
      // Fallback al weight guardado si no existe en rubricWeights
      if (el.weight !== undefined && el.weight !== null) {
        return el.weight;
      }
      // Último fallback: peso por defecto de 1
      return 1;
    };

    // Calcular la suma de todos los weights para normalizar
    const totalWeight = elementEvaluations.reduce((sum, el) => {
      const weight = getElementWeight(el);
      return sum + weight;
    }, 0);

    if (totalWeight === 0) {
      console.error('[AcademicEvaluationService] totalWeight es 0, no se puede calcular porcentaje');
      return 0;
    }

    // Calcular el porcentaje ponderado: suma de (porcentaje * weight) / totalWeight
    const weightedSum = elementEvaluations.reduce((sum, el) => {
      const weight = getElementWeight(el);
      const percentage = Math.min(el.percentage, 100); // Asegurar que no exceda 100%
      return sum + (percentage * weight);
    }, 0);

    const overallPercentage = weightedSum / totalWeight;

    // Log detallado de los pesos usados en el cálculo
    const elementDetails = elementEvaluations.map(el => {
      const weight = getElementWeight(el);
      return {
        name: el.name,
        percentage: el.percentage,
        weight: weight,
        weightSource: rubricWeights && el.name && rubricWeights[el.name] !== undefined 
          ? 'rubricWeights' 
          : el.weight !== undefined 
            ? 'saved_weight' 
            : 'default'
      };
    });

    // console.log(
    //   `[AcademicEvaluationService] calculateOverallScore - ` +
    //   `totalWeight: ${totalWeight}, weightedSum: ${weightedSum}, overallPercentage: ${overallPercentage}%`
    // );
    // console.log(
    //   `[AcademicEvaluationService] Detalles de elementos:`,
    //   JSON.stringify(elementDetails, null, 2)
    // );

    // Asegurar que el porcentaje no exceda 100%
    const clampedPercentage = Math.min(overallPercentage, 100);
    return Math.round(clampedPercentage * 100) / 100;
  }

  /**
   * Guarda evaluación en el progreso del usuario
   */
  async saveEvaluation(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    evaluation: RubricEvaluation,
    conversationId?: string
  ): Promise<boolean> {
    try {
      const evaluationWithConversation = {
        ...evaluation,
        conversation_id: conversationId,
      };

      const updateData: any = {
        rubric_evaluation: evaluationWithConversation,
        final_score: evaluation.overall_score,
        completion_date: new Date().toISOString(),
      };

      // Nota: approved_on_attempt no está en el esquema actual de la base de datos
      // Si se necesita en el futuro, agregar la columna al esquema SQL primero

      const { error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .update(updateData)
        .eq('user_email', email)
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson);

      if (error) {
        // console.error('[AcademicEvaluationService] Error al guardar evaluación:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[AcademicEvaluationService] Error al guardar evaluación:', error);
      return false;
    }
  }
}

export const academicEvaluationService = new AcademicEvaluationService();
export default academicEvaluationService;

